import React, { Fragment, useState, useCallback, useRef, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Spinner, Badge, Table } from 'reactstrap';
import { Breadcrumbs } from '../../../AbstractElements';
import { Upload, CheckCircle, XCircle, Save, AlertTriangle, FileText, Layers, Plus } from 'react-feather';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../../Config/api';

const StoreInventoryImport = () => {
  console.log('StoreInventoryImport component rendering');
  const [searchParams] = useSearchParams();
  const storeParam = searchParams.get('store');

  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [saveResult, setSaveResult] = useState(null);
  const [activeTab, setActiveTab] = useState('matched');
  const [selectedItems, setSelectedItems] = useState({});
  const fileInputRef = useRef(null);

  // Log when file changes
  useEffect(() => {
    console.log('File state changed:', file?.name || 'no file');
  }, [file]);

  // Import mode: 'multi' for SKU by delivery month format, 'single' for single-store format
  const [importMode, setImportMode] = useState('multi');

  // For single-store mode
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [loadingStores, setLoadingStores] = useState(true);

  // Inventory summary
  const [inventorySummary, setInventorySummary] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);

  // Fetch stores on mount
  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoadingStores(true);
        const response = await axios.get(`${API_BASE_URL}/api/stores`);
        const storesList = response.data.stores || [];
        setStores(storesList);

        // If store=goa query param, set to single mode and pre-select Goa India Store
        if (storeParam === 'goa') {
          setImportMode('single');
          const goaStore = storesList.find(s => s.name === 'Goa India Store' || s.code === 'GOA');
          if (goaStore) {
            setSelectedStore(goaStore.id.toString());
          }
        }
      } catch (err) {
        console.error('Error fetching stores:', err);
      } finally {
        setLoadingStores(false);
      }
    };
    fetchStores();
  }, [storeParam]);

  // Fetch inventory summary
  const fetchInventorySummary = async () => {
    try {
      setLoadingInventory(true);
      const response = await axios.get(`${API_BASE_URL}/api/store-inventory/summary`);
      setInventorySummary(response.data.summary || []);
    } catch (err) {
      console.error('Error fetching inventory summary:', err);
    } finally {
      setLoadingInventory(false);
    }
  };

  // Fetch inventory on mount
  useEffect(() => {
    fetchInventorySummary();
  }, []);

  // Prevent browser from opening dropped files
  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);

    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, []);

  // Initialize all items as selected when parseResult changes
  useEffect(() => {
    if (parseResult && parseResult.matchedItems) {
      const initialSelected = {};
      parseResult.matchedItems.forEach((_, index) => {
        initialSelected[index] = true;
      });
      setSelectedItems(initialSelected);
    }
  }, [parseResult]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      const droppedFile = files[0];
      const fileName = droppedFile.name.toLowerCase();
      if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        console.log('File dropped:', droppedFile.name);
        setFile(droppedFile);
        setParseResult(null);
        setSaveResult(null);
      } else {
        alert('Please upload an Excel file (.xlsx or .xls)');
      }
    }
    return false;
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setParseResult(null);
      setSaveResult(null);
    }
  };

  const handleParseFile = async () => {
    if (!file) {
      alert('Please select a file first');
      return;
    }

    console.log('Starting file parse:', file.name);
    setLoading(true);
    setParseResult(null);
    setSaveResult(null);
    setSelectedItems({});

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Use multi-store endpoint for SKU by delivery month format
      const endpoint = importMode === 'multi'
        ? `${API_BASE_URL}/api/store-inventory/parse-multi-store-excel`
        : `${API_BASE_URL}/api/store-inventory/parse-excel`;

      console.log('Uploading to:', endpoint);

      if (importMode === 'single') {
        const store = stores.find(s => s.id.toString() === selectedStore);
        formData.append('storeName', store?.name || '');
        formData.append('storeId', selectedStore);
      }

      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60 second timeout
      });

      console.log('Parse response:', response.data.summary);
      setParseResult({ ...response.data, mode: importMode });
    } catch (err) {
      console.error('Parse error:', err);
      alert('Failed to parse file: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleItem = (index) => {
    setSelectedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleSelectAll = (checked) => {
    if (parseResult && parseResult.matchedItems) {
      const newSelected = {};
      parseResult.matchedItems.forEach((_, index) => {
        newSelected[index] = checked;
      });
      setSelectedItems(newSelected);
    }
  };

  const getSelectedCount = () => {
    return Object.values(selectedItems).filter(Boolean).length;
  };

  const isAllSelected = () => {
    if (!parseResult || !parseResult.matchedItems || parseResult.matchedItems.length === 0) return false;
    return parseResult.matchedItems.every((_, index) => selectedItems[index]);
  };

  const handleSaveInventory = async () => {
    if (!parseResult || !parseResult.matchedItems) return;

    setSaving(true);
    setSaveResult(null);

    try {
      const itemsToSave = parseResult.matchedItems
        .map((item, index) => ({ item, index }))
        .filter(({ index }) => selectedItems[index])
        .map(({ item }) => ({
          productId: item.productId,
          sku: item.sku,
          storeStock: item.storeStock
        }));

      const response = await axios.post(`${API_BASE_URL}/api/store-inventory/save-multi-store`, {
        items: itemsToSave
      });

      setSaveResult(response.data);
      // Refresh inventory summary after save
      fetchInventorySummary();
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Get store names from parse result
  const getStoreNames = () => {
    if (!parseResult || !parseResult.stores) return [];
    return parseResult.stores.map(s => s.name);
  };

  const styles = `
    .upload-zone {
      border: 2px dashed #dee2e6;
      border-radius: 12px;
      padding: 40px;
      text-align: center;
      background: #f8f9fa;
      transition: all 0.3s ease;
      cursor: pointer;
    }

    .upload-zone:hover,
    .upload-zone.drag-active {
      border-color: #7366ff;
      background: #f0eeff;
    }

    .upload-zone.has-file {
      border-color: #54ba4a;
      background: #f0fff0;
    }

    .upload-icon-wrapper {
      width: 70px;
      height: 70px;
      margin: 0 auto 20px;
      background: linear-gradient(135deg, #7366ff 0%, #a389ff 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 15px rgba(115, 102, 255, 0.3);
    }

    .upload-zone.has-file .upload-icon-wrapper {
      background: linear-gradient(135deg, #54ba4a 0%, #7ed56f 100%);
      box-shadow: 0 4px 15px rgba(84, 186, 74, 0.3);
    }

    .upload-icon-wrapper svg {
      color: white;
      width: 28px;
      height: 28px;
    }

    .upload-title {
      font-size: 18px;
      font-weight: 600;
      color: #2c323f;
      margin-bottom: 8px;
    }

    .upload-subtitle {
      color: #6c757d;
      font-size: 14px;
    }

    .file-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #e8f5e9;
      color: #2e7d32;
      padding: 8px 16px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 500;
      margin-top: 15px;
    }

    .summary-card {
      background: white;
      border-radius: 12px;
      padding: 25px;
      text-align: center;
      box-shadow: 0 2px 10px rgba(0,0,0,0.05);
      border: 1px solid #eee;
    }

    .summary-card.matched {
      border-left: 4px solid #54ba4a;
    }

    .summary-card.unmatched {
      border-left: 4px solid #fc4438;
    }

    .summary-card.total {
      border-left: 4px solid #7366ff;
    }

    .summary-number {
      font-size: 36px;
      font-weight: 700;
      line-height: 1;
      margin-bottom: 8px;
    }

    .summary-card.total .summary-number {
      color: #7366ff;
    }

    .summary-card.matched .summary-number {
      color: #54ba4a;
    }

    .summary-card.unmatched .summary-number {
      color: #fc4438;
    }

    .summary-label {
      color: #6c757d;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 1px;
      font-weight: 600;
    }

    .tab-buttons {
      display: flex;
      gap: 10px;
      margin-bottom: 20px;
    }

    .tab-btn {
      padding: 10px 20px;
      border: 1px solid #dee2e6;
      background: white;
      color: #6c757d;
      font-size: 14px;
      font-weight: 500;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .tab-btn:hover {
      background: #f8f9fa;
    }

    .tab-btn.active {
      background: #7366ff;
      border-color: #7366ff;
      color: white;
    }

    .tab-btn .count-badge {
      background: rgba(0,0,0,0.1);
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 12px;
    }

    .tab-btn.active .count-badge {
      background: rgba(255,255,255,0.2);
    }

    .data-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 12px;
    }

    .data-table th {
      background: #f8f9fa;
      padding: 10px 8px;
      text-align: left;
      font-size: 10px;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border-bottom: 2px solid #eee;
      position: sticky;
      top: 0;
      z-index: 1;
    }

    .data-table td {
      padding: 8px;
      color: #2c323f;
      font-size: 12px;
      border-bottom: 1px solid #eee;
      vertical-align: middle;
    }

    .data-table tr:hover td {
      background: #f8f9fa;
    }

    .data-table tr.not-selected td {
      opacity: 0.5;
    }

    .sku-tag {
      display: inline-block;
      background: #e8eaf6;
      color: #3f51b5;
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      font-family: monospace;
    }

    .store-stock {
      text-align: center;
      font-weight: 600;
    }

    .store-stock.has-stock {
      color: #28a745;
    }

    .store-stock.no-stock {
      color: #ccc;
    }

    .btn-parse {
      background: linear-gradient(135deg, #7366ff 0%, #a389ff 100%);
      border: none;
      color: white;
      padding: 12px 30px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 14px;
      margin-top: 20px;
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(115, 102, 255, 0.3);
    }

    .btn-parse:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(115, 102, 255, 0.4);
    }

    .btn-parse:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    .btn-save {
      background: linear-gradient(135deg, #54ba4a 0%, #7ed56f 100%);
      border: none;
      color: white;
      padding: 12px 35px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 14px;
      margin-top: 20px;
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(84, 186, 74, 0.3);
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .btn-save:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(84, 186, 74, 0.4);
    }

    .btn-save:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    .success-alert {
      background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
      border: 1px solid #a5d6a7;
      border-radius: 12px;
      padding: 20px 25px;
      display: flex;
      align-items: center;
      gap: 15px;
      margin-top: 25px;
    }

    .success-alert svg {
      color: #2e7d32;
      width: 28px;
      height: 28px;
      flex-shrink: 0;
    }

    .success-alert .message {
      color: #1b5e20;
      font-weight: 600;
      font-size: 15px;
    }

    .success-alert .details {
      color: #388e3c;
      font-size: 13px;
      margin-top: 3px;
    }

    .table-container {
      max-height: 500px;
      overflow-y: auto;
      border-radius: 8px;
      border: 1px solid #eee;
    }

    .selection-info {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 15px;
      background: #f8f9fa;
      border-radius: 8px;
      margin-bottom: 15px;
    }

    .selection-info .count {
      font-weight: 600;
      color: #7366ff;
    }

    .checkbox-cell {
      width: 40px;
      text-align: center;
    }

    .checkbox-cell input[type="checkbox"] {
      width: 18px;
      height: 18px;
      cursor: pointer;
      accent-color: #7366ff;
    }

    .store-summary-cards {
      display: flex;
      gap: 15px;
      flex-wrap: wrap;
      margin-bottom: 20px;
    }

    .store-summary-card {
      background: white;
      border: 1px solid #eee;
      border-radius: 10px;
      padding: 15px 20px;
      min-width: 140px;
      text-align: center;
    }

    .store-summary-card .store-name {
      font-weight: 600;
      color: #333;
      font-size: 14px;
      margin-bottom: 8px;
    }

    .store-summary-card .store-total {
      font-size: 24px;
      font-weight: 700;
      color: #7366ff;
    }

    .store-summary-card .store-items {
      font-size: 11px;
      color: #6c757d;
      margin-top: 4px;
    }

    .mode-toggle {
      display: flex;
      gap: 10px;
      margin-bottom: 20px;
    }

    .mode-btn {
      padding: 8px 16px;
      border: 2px solid #dee2e6;
      background: white;
      border-radius: 8px;
      cursor: pointer;
      font-size: 13px;
      font-weight: 500;
      transition: all 0.2s ease;
    }

    .mode-btn:hover {
      border-color: #7366ff;
    }

    .mode-btn.active {
      border-color: #7366ff;
      background: #7366ff;
      color: white;
    }
  `;

  return (
    <Fragment>
      <style>{styles}</style>
      <Breadcrumbs mainTitle="Store Inventory Import" parent="Products" title="Import Stock by SKU" />
      <Container fluid={true}>
        {/* Current Inventory Summary */}
        <Row className="mb-4">
          <Col sm="12">
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5>Current Inventory by Store</h5>
                    <span>Overview of stock levels across all stores</span>
                  </div>
                  <Button
                    color="light"
                    size="sm"
                    onClick={fetchInventorySummary}
                    disabled={loadingInventory}
                  >
                    {loadingInventory ? <Spinner size="sm" /> : <i className="fa fa-refresh"></i>}
                    {' '}Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {loadingInventory ? (
                  <div className="text-center py-3">
                    <Spinner /> Loading inventory...
                  </div>
                ) : inventorySummary.length === 0 ? (
                  <div className="text-center py-3 text-muted">
                    No inventory data yet. Upload an Excel file to import stock.
                  </div>
                ) : (
                  <div className="d-flex flex-wrap gap-3">
                    {inventorySummary.map(store => (
                      <div
                        key={store.store_id}
                        style={{
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                          borderRadius: '12px',
                          padding: '20px',
                          minWidth: '180px',
                          border: '1px solid #dee2e6',
                          textAlign: 'center'
                        }}
                      >
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '10px' }}>
                          {store.store_name}
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: '700', color: '#7366ff' }}>
                          {store.total_stock || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
                          {store.sku_count || 0} SKUs
                        </div>
                        {store.last_updated && (
                          <div style={{ fontSize: '10px', color: '#adb5bd', marginTop: '8px' }}>
                            Updated: {new Date(store.last_updated).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col sm="12">
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div>
                    <h5>Upload Excel File</h5>
                    <span>Import SKU stock data for multiple stores</span>
                  </div>
                  <div className="mode-toggle">
                    <button
                      className={`mode-btn ${importMode === 'multi' ? 'active' : ''}`}
                      onClick={() => { setImportMode('multi'); setParseResult(null); }}
                    >
                      Multi-Store (SKU by Delivery)
                    </button>
                    <button
                      className={`mode-btn ${importMode === 'single' ? 'active' : ''}`}
                      onClick={() => { setImportMode('single'); setParseResult(null); }}
                    >
                      Single Store
                    </button>
                  </div>
                </div>

                {importMode === 'single' && (
                  <div className="mt-3" style={{ maxWidth: '300px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#6c757d', marginBottom: '4px', display: 'block' }}>
                      SELECT STORE *
                    </label>
                    {loadingStores ? (
                      <div style={{ padding: '8px', color: '#6c757d' }}>Loading stores...</div>
                    ) : (
                      <select
                        value={selectedStore}
                        onChange={(e) => setSelectedStore(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: selectedStore ? '2px solid #54ba4a' : '2px solid #dee2e6',
                          fontSize: '14px',
                          backgroundColor: selectedStore ? '#f0fff0' : '#fff',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">-- Select a Store --</option>
                        {stores.map(store => (
                          <option key={store.id} value={store.id}>
                            {store.name} {store.location ? `(${store.location})` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardBody>
                {/* Upload Zone */}
                <div
                  className={`upload-zone ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <div className="upload-icon-wrapper">
                    {file ? <FileText /> : <Upload />}
                  </div>
                  <div className="upload-title">
                    {file ? 'File Selected' : 'Drop Excel File Here'}
                  </div>
                  <div className="upload-subtitle">
                    {file ? 'Click to change file' : 'Expected format: SKU BY DELIVERY MONTH.xlsx'}
                  </div>
                  {file && (
                    <div className="file-badge">
                      <FileText size={16} />
                      {file.name}
                    </div>
                  )}
                </div>

                {file && !parseResult && (
                  <div style={{ textAlign: 'center' }}>
                    {importMode === 'single' && !selectedStore && (
                      <div style={{
                        padding: '12px 20px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '8px',
                        color: '#856404',
                        marginTop: '15px',
                        display: 'inline-block'
                      }}>
                        <AlertTriangle size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                        Please select a store before parsing
                      </div>
                    )}
                    <br />
                    <Button
                      className="btn-parse"
                      onClick={handleParseFile}
                      disabled={loading || (importMode === 'single' && !selectedStore)}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" style={{ marginRight: '8px' }} />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Layers size={16} style={{ marginRight: '8px' }} />
                          Parse & Match SKUs
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Results */}
            {parseResult && (
              <>
                {/* Summary Cards */}
                <Row className="mb-4">
                  <Col md="4">
                    <div className="summary-card total">
                      <div className="summary-number">{parseResult.summary.totalRows}</div>
                      <div className="summary-label">Total Rows</div>
                    </div>
                  </Col>
                  <Col md="4">
                    <div className="summary-card matched">
                      <div className="summary-number">{parseResult.summary.matched}</div>
                      <div className="summary-label">Matched SKUs</div>
                    </div>
                  </Col>
                  <Col md="4">
                    <div className="summary-card unmatched">
                      <div className="summary-number">{parseResult.summary.unmatched}</div>
                      <div className="summary-label">Unmatched</div>
                    </div>
                  </Col>
                </Row>

                {/* Store Stock Summary */}
                {parseResult.storeStockSummary && (
                  <Card className="mb-4">
                    <CardBody>
                      <h6 className="mb-3">Stock Summary by Store</h6>
                      <div className="store-summary-cards">
                        {Object.entries(parseResult.storeStockSummary).map(([storeName, data]) => (
                          <div key={storeName} className="store-summary-card">
                            <div className="store-name">{storeName}</div>
                            <div className="store-total">{data.total}</div>
                            <div className="store-items">{data.items} SKUs</div>
                          </div>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                )}

                <Card>
                  <CardBody>
                    {/* Tabs */}
                    <div className="tab-buttons">
                      <button
                        className={`tab-btn ${activeTab === 'matched' ? 'active' : ''}`}
                        onClick={() => setActiveTab('matched')}
                      >
                        <CheckCircle size={16} />
                        Matched
                        <span className="count-badge">{parseResult.matchedItems.length}</span>
                      </button>
                      <button
                        className={`tab-btn ${activeTab === 'unmatched' ? 'active' : ''}`}
                        onClick={() => setActiveTab('unmatched')}
                      >
                        <AlertTriangle size={16} />
                        Unmatched
                        <span className="count-badge">{parseResult.unmatchedItems.length}</span>
                      </button>
                    </div>

                    {/* Matched Items Table */}
                    {activeTab === 'matched' && (
                      <>
                        <div className="selection-info">
                          <span>
                            <span className="count">{getSelectedCount()}</span> of {parseResult.matchedItems.length} SKUs selected for import
                          </span>
                        </div>
                        <div className="table-container">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th className="checkbox-cell">
                                  <input
                                    type="checkbox"
                                    checked={isAllSelected()}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    title="Select All"
                                  />
                                </th>
                                <th>SKU</th>
                                <th>Title</th>
                                <th>Colour</th>
                                <th>Fabric</th>
                                <th>Size</th>
                                <th style={{ textAlign: 'center' }}>Total</th>
                                {getStoreNames().map(storeName => (
                                  <th key={storeName} style={{ textAlign: 'center' }}>{storeName}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {parseResult.matchedItems.map((item, index) => (
                                <tr key={index} className={!selectedItems[index] ? 'not-selected' : ''}>
                                  <td className="checkbox-cell">
                                    <input
                                      type="checkbox"
                                      checked={selectedItems[index] || false}
                                      onChange={() => handleToggleItem(index)}
                                    />
                                  </td>
                                  <td><span className="sku-tag">{item.sku}</span></td>
                                  <td>{item.title}</td>
                                  <td>{item.colour}</td>
                                  <td>{item.fabric}</td>
                                  <td>{item.size}</td>
                                  <td style={{ textAlign: 'center', fontWeight: 600 }}>{item.grandTotal}</td>
                                  {getStoreNames().map(storeName => {
                                    const stock = item.storeStock?.[storeName] || 0;
                                    return (
                                      <td key={storeName} className={`store-stock ${stock > 0 ? 'has-stock' : 'no-stock'}`}>
                                        {stock > 0 ? stock : '-'}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Save Button */}
                        {parseResult.matchedItems.length > 0 && !saveResult && (
                          <div style={{ textAlign: 'center' }}>
                            <Button
                              className="btn-save"
                              onClick={handleSaveInventory}
                              disabled={saving || getSelectedCount() === 0}
                            >
                              {saving ? (
                                <>
                                  <Spinner size="sm" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Save size={16} />
                                  Save Stock for {getSelectedCount()} SKUs
                                </>
                              )}
                            </Button>
                          </div>
                        )}

                        {/* Success Alert */}
                        {saveResult && (
                          <div className="success-alert">
                            <CheckCircle />
                            <div>
                              <div className="message">{saveResult.message}</div>
                              <div className="details">
                                {saveResult.savedCount} inventory records saved
                                {saveResult.errorCount > 0 && `, ${saveResult.errorCount} errors`}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Unmatched Items Table */}
                    {activeTab === 'unmatched' && (
                      <>
                        {parseResult.unmatchedItems.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '50px 20px', color: '#6c757d' }}>
                            <CheckCircle size={48} color="#54ba4a" style={{ marginBottom: '15px' }} />
                            <p>All SKUs matched successfully!</p>
                          </div>
                        ) : (
                          <>
                            <div style={{
                              padding: '12px 15px',
                              backgroundColor: '#fff3cd',
                              border: '1px solid #ffc107',
                              borderRadius: '8px',
                              marginBottom: '15px',
                              fontSize: '13px',
                              color: '#856404'
                            }}>
                              <AlertTriangle size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                              These SKUs were not found in the product database. They need to be created first.
                            </div>
                            <div className="table-container">
                              <table className="data-table">
                                <thead>
                                  <tr>
                                    <th>SKU</th>
                                    <th>Title</th>
                                    <th>Colour</th>
                                    <th>Fabric</th>
                                    <th>Size</th>
                                    <th style={{ textAlign: 'center' }}>Total</th>
                                    {getStoreNames().map(storeName => (
                                      <th key={storeName} style={{ textAlign: 'center' }}>{storeName}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {parseResult.unmatchedItems.map((item, index) => (
                                    <tr key={index}>
                                      <td><span className="sku-tag" style={{ background: '#ffebee', color: '#c62828' }}>{item.sku}</span></td>
                                      <td>{item.title}</td>
                                      <td>{item.colour}</td>
                                      <td>{item.fabric}</td>
                                      <td>{item.size}</td>
                                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{item.grandTotal}</td>
                                      {getStoreNames().map(storeName => {
                                        const stock = item.storeStock?.[storeName] || 0;
                                        return (
                                          <td key={storeName} className={`store-stock ${stock > 0 ? 'has-stock' : 'no-stock'}`}>
                                            {stock > 0 ? stock : '-'}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </CardBody>
                </Card>
              </>
            )}
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default StoreInventoryImport;
